import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Logo } from "@/components/layout/Logo"

interface AuthLayoutProps {
  title: string
  description?: React.ReactNode
  /** Título da aba do navegador */
  pageTitle: string
  children: React.ReactNode
  /** Links abaixo do formulário (ex.: "Não tem conta? Criar conta") */
  footer?: React.ReactNode
}

const HIGHLIGHTS = [
  "Importe o extrato do banco em vez de digitar lançamento por lançamento.",
  "Acompanhe cartões, parcelas, metas e investimentos no mesmo lugar.",
  "Verificação em duas etapas e controle dos dispositivos conectados.",
]

/** Moldura das páginas de acesso: formulário à esquerda e, no desktop, o resumo do produto à direita. */
export function AuthLayout({ title, description, pageTitle, children, footer }: AuthLayoutProps) {
  useEffect(() => { document.title = `${pageTitle} · FinControl` }, [pageTitle])

  return (
    <div className="min-h-dvh bg-background text-foreground grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
      <div className="flex flex-col px-4 sm:px-8 py-6 md:py-8">
        <Link to="/" aria-label="FinControl, página inicial" className="self-start"><Logo /></Link>

        <main className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-[400px]">
            <h1 className="text-[26px] leading-[32px] font-semibold tracking-[-0.025em]">{title}</h1>
            {description && <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">{description}</p>}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-8 text-[14px] text-muted-foreground space-y-2">{footer}</div>}
          </div>
        </main>

        <nav aria-label="Documentos" className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
          <span>© {new Date().getFullYear()} FinControl</span>
          <Link to="/privacidade" className="hover:text-foreground">Privacidade</Link>
          <Link to="/termos" className="hover:text-foreground">Termos de Uso</Link>
        </nav>
      </div>

      <aside className="hidden lg:flex flex-col justify-between bg-positive-900 text-white p-12 xl:p-16" aria-label="Sobre o FinControl">
        <div />
        <div className="max-w-md">
          <p className="text-[28px] leading-[36px] font-semibold tracking-[-0.025em]">
            Suas finanças inteiras em um só lugar.
          </p>
          <ul className="mt-8 space-y-4 text-[15px] text-positive-100 leading-relaxed">
            {HIGHLIGHTS.map(h => (
              <li key={h} className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-positive-300 shrink-0" aria-hidden="true" />
                {h}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-white/15 bg-white/5 p-5 max-w-md" aria-hidden="true">
          <p className="text-[12px] text-positive-100">Patrimônio líquido</p>
          <p className="num text-[26px] font-semibold tracking-[-0.02em] mt-1">R$ 187.420,55</p>
          <div className="mt-4 flex h-1.5 gap-[2px] overflow-hidden rounded-sm">
            <span className="bg-positive-300" style={{ width: "34%" }} /><span className="bg-info-300" style={{ width: "62%" }} /><span className="bg-warning-300" style={{ width: "4%" }} />
          </div>
          <p className="mt-3 text-[12px] text-positive-100">Dados ilustrativos</p>
        </div>
      </aside>
    </div>
  )
}
